import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, type ReviewViewScene } from '../../lib/api.js';
import {
  ReviewRequirementsScreen,
  type ReviewRequirementsData,
} from '../../components/ReviewRequirementsScreen.js';
import {
  AnalyseWithAi,
  extractionFailureSentence,
  parseFailure,
  reviewRunStateOf,
  scenesThatDidNotFit,
  scenesWithoutElements,
  shouldOfferAnalysis,
} from '../../components/AnalyseWithAi.js';
import { BreakdownProgress } from '../../components/BreakdownProgress.js';
import {
  BulkSceneActions,
  type BulkSceneAction,
  type BulkSceneResult,
} from '../../components/BulkSceneActions.js';
import { useScriptRun } from '../../lib/useScriptRun.js';
import type { RequirementDecision, SceneCardScene, SceneLine } from '../../components/SceneCard.js';
import type { RailScene, SceneConfidence } from '../../components/SceneRail.js';

/**
 * Screenplay layout from the text itself.
 *
 * A character cue is a short all-caps line; dialogue is what follows it until a
 * blank line. This is a rendering convenience, not a parse: the authoritative
 * structure is the screenplay file, and guessing wrong here costs alignment,
 * not meaning. On the Locations page nothing is painted on the text: the element
 * colours belong to the detailed breakdown (`components/elements/`).
 */
function toLines(text: string): SceneLine[] {
  const raw = text.split('\n');
  const lines: SceneLine[] = [];
  let inDialogue = false;

  for (const line of raw) {
    const trimmed = line.trim();

    if (trimmed === '') {
      inDialogue = false;
      lines.push({ kind: 'ACTION', spans: [{ text: '' }] });
      continue;
    }

    const isCue =
      trimmed.length <= 40 && trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed);

    const kind: SceneLine['kind'] = isCue
      ? 'CHARACTER'
      : trimmed.startsWith('(')
        ? 'PARENTHETICAL'
        : inDialogue
          ? 'DIALOGUE'
          : 'ACTION';

    if (isCue) inDialogue = true;

    lines.push({ kind, spans: [{ text: line }] });
  }

  return lines;
}

function confidenceOf(scene: ReviewViewScene): SceneConfidence {
  if (scene.requirements.length === 0) return 'NOT_RECOGNISED';
  return scene.requirements.some((r) => r.confidenceBand !== 'HIGH')
    ? 'LOW_CONFIDENCE'
    : 'RESOLVED';
}

const UNDECIDED = new Set(['PROPOSED', 'DRAFT']);

// The run-state rules moved beside `extractionHasRun` (the scene breakdown workspace reads them
// too); re-exported so every existing import keeps working.
export { extractionFailureSentence, reviewRunStateOf } from '../../components/AnalyseWithAi.js';

/**
 * Review requirements, wired to the workspace.
 *
 * The screen itself is presentational and takes data; this is the half that
 * knows about the API, so the layout can be exercised without a server and the
 * fetching can change without touching it.
 */
export function ReviewRequirements({
  projectId,
  scriptVersionId,
  projectTitle,
  scriptVersionLabel,
  onContinueToMatching,
  continueBlockedReason = null,
  continueNote = null,
  continueLabel,
  initialSceneId = null,
  onSceneChange,
}: {
  projectId: string;
  scriptVersionId: string;
  projectTitle: string;
  scriptVersionLabel: string;
  onContinueToMatching?: () => void;
  continueBlockedReason?: string | null;
  continueNote?: string | null;
  continueLabel?: string;
  /** The scene to open on — a route's `?scene=`. */
  initialSceneId?: string | null;
  /** Told when the scene on screen changes, so a route can keep it in the address. */
  onSceneChange?: (sceneId: string) => void;
}) {
  const queryClient = useQueryClient();

  /**
   * THIS SCREEN OWNS ITS OWN RE-READ, because on step 3 nothing else is
   * mounted to do it.
   *
   * A producer watched a run finish COMPLETED with 85 requirements written
   * while the page went on reading "0 location requirements across 120
   * scenes", because the answer on screen was fetched before any of them
   * existed. UNTIL THAT RE-READ LANDS, THE RUN IS STILL "READING" (stage,
   * 2026-10-30): in that moment the screen held the pre-run answer.
   */
  const [rereading, setRereading] = useState(false);
  const { progress, inFlight } = useScriptRun(scriptVersionId, {
    onFinished: () => {
      setRereading(true);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
        queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
      ]).finally(() => {
        setRereading(false);
      });
    },
  });
  const reading = inFlight || rereading;
  const didNotFit = scenesThatDidNotFit(progress);
  const withoutElements = scenesWithoutElements(progress);

  const review = useQuery({
    queryKey: ['review-view', scriptVersionId],
    queryFn: () => api.reviewView(scriptVersionId),
  });

  // The requirement rows themselves — the same query the location list runs.
  // The review view names a scene's requirements; this carries the row
  // version every approve and dismiss must present, and the status that is
  // current rather than the one the view was rendered from.
  const requirements = useQuery({
    queryKey: ['requirements', projectId],
    queryFn: () => api.listRequirements(projectId),
  });

  const refreshDecisions = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
      queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
    ]);
  };

  /** Undo a dismissal from the scene card: back to awaiting a decision. */
  const restore = useMutation({
    mutationFn: (requirementId: string) => {
      const row = requirements.data?.find((r) => r.id === requirementId);
      if (row === undefined) throw new Error('That requirement is not loaded yet — try again in a moment.');

      return api.restoreRequirement(projectId, row.id, row.rowVersion);
    },
    onSuccess: refreshDecisions,
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) await refreshDecisions();
    },
  });

  /**
   * Approve or dismiss — compare-and-swap on the row version, so a decision
   * made against a stale row is refused rather than overwriting somebody
   * else's; on that refusal the rows are re-read.
   */
  const decide = useMutation({
    mutationFn: (input: { requirementId: string; decision: RequirementDecision }) => {
      const row = requirements.data?.find((r) => r.id === input.requirementId);
      if (row === undefined) throw new Error('That requirement is not loaded yet — try again in a moment.');

      return input.decision === 'APPROVE'
        ? api.approveRequirement(projectId, row.id, row.rowVersion)
        : api.dismissRequirement(projectId, row.id, row.rowVersion);
    },
    onSuccess: refreshDecisions,
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) await refreshDecisions();
    },
  });

  /**
   * MERGE AND SPLIT: the model's most consequential mistakes are a place read
   * twice or two places read as one. `settled` closes their dialog only once the
   * server has agreed — closing on the click threw away a selection.
   */
  const [settled, setSettled] = useState(0);
  const landed = async () => {
    setSettled((n) => n + 1);
    await refreshDecisions();
  };

  const merge = useMutation({
    mutationFn: (requirementIds: readonly string[]) => api.mergeRequirements(projectId, requirementIds),
    onSuccess: landed,
  });

  const split = useMutation({
    mutationFn: (input: { requirementId: string; title: string; sceneIds: readonly string[] }) =>
      api.splitRequirement(projectId, input.requirementId, { title: input.title, sceneIds: input.sceneIds }),
    onSuccess: landed,
  });

  /** A phrase the producer selected, marked as a demand on the location. */
  const tagFacet = useMutation({
    mutationFn: (input: { requirementId: string; facet: string; label: string }) =>
      api.addConstraint(projectId, input.requirementId, { facet: input.facet, label: input.label }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] }),
        queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      ]);
    },
  });

  const addRequirement = useMutation({
    mutationFn: (input: { title: string; sceneIds: readonly string[] }) =>
      api.addRequirement(projectId, { title: input.title, sceneIds: input.sceneIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['review-view', scriptVersionId] });
    },
  });

  /**
   * SCENES PICKED ON THE RAIL, for the actions that already exist one at a
   * time (owner review 2026-10-27, point 5): a set of scene ids in component
   * state — a few seconds of intent before a press, not a view to return to.
   */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BulkSceneResult | null>(null);

  const pickedScenes = (review.data?.scenes ?? []).filter((s) => picked.has(s.id));
  // The undecided requirements on them, once each, read from the CURRENT rows.
  const undecidedOnPicked = [...new Set(pickedScenes.flatMap((s) => s.requirements.map((r) => r.id)))]
    .map((id) => requirements.data?.find((row) => row.id === id))
    .filter((row): row is NonNullable<typeof row> => row !== undefined)
    .filter((row) => row.status === 'PROPOSED' || row.status === 'DRAFT');
  const unreviewedPicked = pickedScenes.filter((s) => s.reviewStatus !== 'REVIEWED');

  const bulk = useMutation({
    mutationFn: async (action: BulkSceneAction): Promise<BulkSceneResult> => {
      const refused: BulkSceneResult['refused'] = [];
      let done = 0;
      const reason = (error: unknown) =>
        error instanceof Error && error.message !== '' ? error.message : 'Refused — try it on its own card.';

      // One at a time, through the verbs a single press uses, so each row
      // keeps its own version check and its own answer.
      if (action === 'REVIEW') {
        for (const s of unreviewedPicked) {
          try {
            await api.markSceneReviewed(projectId, s.id);
            done += 1;
          } catch (error) {
            refused.push({ label: `Scene ${s.sceneNumber ?? String(s.ordinal)}`, reason: reason(error) });
          }
        }
      } else {
        for (const row of undecidedOnPicked) {
          try {
            if (action === 'APPROVE') await api.approveRequirement(projectId, row.id, row.rowVersion);
            else await api.dismissRequirement(projectId, row.id, row.rowVersion);
            done += 1;
          } catch (error) {
            refused.push({ label: row.title, reason: reason(error) });
          }
        }
      }

      return { action, done, refused };
    },
    onMutate: () => {
      setBulkResult(null);
    },
    onSuccess: async (result) => {
      setBulkResult(result);
      await refreshDecisions();
    },
  });

  const data: ReviewRequirementsData | null = useMemo(() => {
    const view = review.data;
    if (!view) return null;

    const statusById = new Map((requirements.data ?? []).map((r) => [r.id, r.status]));
    // The full rows carry what each requirement must be like — its values and
    // its creative brief — which the review view does not repeat.
    const rowById = new Map((requirements.data ?? []).map((r) => [r.id, r]));

    const scenes: SceneCardScene[] = view.scenes.map((scene) => ({
      id: scene.id,
      sceneNumber: scene.sceneNumber,
      ordinal: scene.ordinal,
      slugline: scene.slugline,
      intExt: scene.intExt,
      timeOfDay: scene.timeOfDay,
      requirements: scene.requirements.map((r) => {
        const row = rowById.get(r.id);

        return {
          id: r.id,
          title: r.title,
          status: statusById.get(r.id) ?? r.status,
          confidenceBand: r.confidenceBand,
          sharedWithSceneNumbers: r.sharedWithSceneNumbers,
          // READ DEFENSIVELY: `facet` is on every constraint row the server
          // sends, and absent from older payloads a rolled-back API answers with.
          constraints: (row?.constraints ?? []).map((c) => {
            const facet: unknown = (c as { facet?: unknown }).facet;

            return { id: c.id, kind: c.kind, facet: typeof facet === 'string' ? facet : null, label: c.label };
          }),
          creativeDescription: row?.creativeDescription ?? null,
        };
      }),
      locationRecognised: scene.requirements.length > 0,
      recognitionNote:
        scene.requirements.length > 0
          ? scene.requirements.map((r) => r.title).join(' · ')
          : 'No location noun resolved for this scene',
      lines: scene.text === null ? [] : toLines(scene.text),
    }));

    const railScenes: RailScene[] = view.scenes.map((scene) => {
      const shared = scene.requirements.flatMap((r) => r.sharedWithSceneNumbers);

      return {
        id: scene.id,
        sceneNumber: scene.sceneNumber,
        ordinal: scene.ordinal,
        slugline: scene.slugline,
        confidence: confidenceOf(scene),
        note:
          scene.requirements.length > 0
            ? scene.requirements.map((r) => r.title).join(' · ')
            : 'No location noun resolved',
        sharedWithSceneNumbers: [...new Set(shared)],
        sharedUnresolved: scene.requirements.length === 0 && shared.length > 0,
        // A scene needs the producer while it has no location, or one of its
        // locations still awaits a decision — read from the CURRENT rows.
        state:
          scene.requirements.length === 0 ||
          scene.requirements.some((r) => UNDECIDED.has(statusById.get(r.id) ?? r.status))
            ? ('ATTENTION' as const)
            : ('REVIEWED' as const),
        requirementCount: scene.requirements.length,
      };
    });

    // This draft's requirements, each once, with the status that is current.
    const draft = view.requirements
      .map((r) => statusById.get(r.id) ?? r.status)
      .filter((status) => status !== 'SUPERSEDED');

    return {
      projectTitle,
      scriptVersionLabel,
      ready: true,
      requirementCount: view.requirements.length,
      shownRequirementCount: view.requirements.length,
      railScenes,
      scenes,
      summary: {
        awaiting: draft.filter((status) => UNDECIDED.has(status)).length,
        approved: draft.filter((status) => status === 'APPROVED').length,
        notNeeded: draft.filter((status) => status === 'DISMISSED').length,
        scenesWithoutRequirement: view.scenes.filter((scene) => scene.requirements.length === 0).length,
      },
    };
  }, [review.data, requirements.data, projectTitle, scriptVersionLabel]);

  const runState = reviewRunStateOf(progress, reading);

  if (review.isPending) {
    return (
      <p className="mm" data-testid="review-loading" style={{ fontSize: 13 }}>
        Loading the breakdown…
      </p>
    );
  }

  if (review.isError || data === null) {
    return (
      <p data-testid="review-load-error" style={{ fontSize: 13, color: 'var(--danger)' }}>
        {review.error instanceof ApiError ? review.error.message : 'The breakdown could not be loaded.'}
      </p>
    );
  }

  return (
    <ReviewRequirementsScreen
      data={data}
      projectId={projectId}
      busy={
        addRequirement.isPending || tagFacet.isPending || merge.isPending || split.isPending || decide.isPending || restore.isPending
      }
      error={
        merge.error instanceof ApiError
          ? merge.error.message
          : split.error instanceof ApiError
            ? split.error.message
            : tagFacet.error instanceof ApiError
              ? tagFacet.error.message
              : addRequirement.error instanceof ApiError
                ? addRequirement.error.message
                : addRequirement.error
                  ? 'That could not be saved.'
                  : decide.error instanceof ApiError || decide.error instanceof Error
                    ? decide.error.message
                    : restore.error instanceof ApiError || restore.error instanceof Error
                      ? restore.error.message
                      : null
      }
      onAddRequirement={(input) => addRequirement.mutate(input)}
      onTag={(sceneId, selection, target) => {
        // A facet belongs to the scene's location requirement, and the card
        // only offers these when there is exactly one to attach to.
        const requirement = review.data?.scenes.find((s) => s.id === sceneId)?.requirements[0];
        if (requirement === undefined) return;

        tagFacet.mutate({ requirementId: requirement.id, facet: target.facet, label: selection });
      }}
      onDecideRequirement={(requirementId, decision) => decide.mutate({ requirementId, decision })}
      onRestoreRequirement={(requirementId) => restore.mutate(requirementId)}
      settled={settled}
      onMerge={(requirementIds) => merge.mutate(requirementIds)}
      onSplit={(requirementId, input) => {
        split.mutate({ requirementId, title: input.title, sceneIds: input.sceneIds });
      }}
      onContinueToMatching={onContinueToMatching}
      continueBlockedReason={continueBlockedReason}
      continueNote={continueNote}
      continueLabel={continueLabel}
      runState={runState}
      runNote={
        runState === 'FAILED'
          ? extractionFailureSentence(progress)
          : runState === 'PARTIAL'
            ? didNotFit > 0
              ? `${String(didNotFit)} ${didNotFit === 1 ? 'scene was' : 'scenes were'} not read — the screenplay is longer than one request can carry. Pick them on the left and read them on their own; what was found stays as it is.`
              : `${String(withoutElements)} ${withoutElements === 1 ? 'scene was' : 'scenes were'} not fully read — the AI got no answer for some of their departments, so their elements are incomplete. Run the analysis again, or tag what is missing by hand; what was found stays as it is.`
            : null
      }
      initialSceneId={initialSceneId}
      onSceneChange={onSceneChange}
      pickedSceneIds={picked}
      onPickScenes={(sceneIds, on) => {
        setBulkResult(null);
        setPicked((current) => {
          const next = new Set(current);

          for (const id of sceneIds) {
            if (on) next.add(id);
            else next.delete(id);
          }

          return next;
        });
      }}
      bulkActions={
        picked.size === 0 ? null : (
          <BulkSceneActions
            pickedCount={pickedScenes.length}
            undecidedCount={undecidedOnPicked.length}
            unreviewedCount={unreviewedPicked.length}
            busy={bulk.isPending}
            result={bulkResult}
            onApprove={() => {
              bulk.mutate('APPROVE');
            }}
            onDismiss={() => {
              bulk.mutate('DISMISS');
            }}
            onMarkReviewed={() => {
              bulk.mutate('REVIEW');
            }}
            onClear={() => {
              setPicked(new Set());
              setBulkResult(null);
            }}
          />
        )
      }
      analyse={
        /*
         * TWO REASONS TO OFFER A READING, and the second is not "the page is
         * empty": a screenplay too long for one request has requirements for its
         * first part and none for the rest. While a run is going this slot
         * carries the indicator instead: offering "Analyse again" beside a
         * running breakdown invites a second paid request for the same answer.
         */
        reading ? (
          <BreakdownProgress scriptVersionId={scriptVersionId} />
        ) : shouldOfferAnalysis({
            requirementCount: data.requirementCount,
            scenesDidNotFit: didNotFit,
            scenesWithoutElements: withoutElements,
            lastRunFailed: progress?.status === 'FAILED' && parseFailure(progress) === null,
          }) ? (
          <AnalyseWithAi
            projectId={projectId}
            versionId={scriptVersionId}
            sceneCount={data.scenes.length}
            reads="LOCATIONS"
            // SCENES PICKED ON THE RAIL ARE READ ON THEIR OWN: a screenplay too
            // long for one request is finished in selections from here.
            sceneIds={picked.size > 0 ? [...picked] : undefined}
            onStarted={() => {
              // The picks have been spent; left ticked they invite a second
              // paid request for the same scenes.
              setPicked(new Set());
              setBulkResult(null);
            }}
          />
        ) : null
      }
    />
  );
}
